import Cross from "@icons/cross.svg?react";
import Trash from "@icons/trash.svg?react";
import { Trans } from "@lingui/react/macro";
import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { DateTime } from "luxon";

import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";
import { logger } from "@/common/services/logger";
import {
  archiveNotification,
  getNotifications,
} from "@/generated/api/notifications";
import { FullscreenModalLayout } from "@/routes/layout/layout.fullscreen";

import { Button } from "../button/button";
import { Spacer } from "../spacer/spacer";
import { Spinner } from "../spinner/spinner";
import { toaster } from "../toast/toast";
import {
  Notification as NotificationComponent,
  Props as NotificationProps,
} from "./notification";
import {
  isNotificationSupported,
  useNotificationsState,
} from "./use-notification-state";

const NotificationList = () => {
  const sidebar = useSidebar();
  const notifications = useSuspenseQuery(notificationsQuery());

  return (
    <FullscreenModalLayout
      headerButton={
        <button onClick={sidebar.actions.toggle}>
          <Cross />
        </button>
      }
    >
      <FullscreenModalLayout.Title>
        <Trans>Notifikace a zprávy</Trans>
      </FullscreenModalLayout.Title>
      {isNotificationSupported() ? (
        <>
          <NotificationState />
          <Spacer className="h-6" />
        </>
      ) : null}
      <div className="flex flex-col gap-y-4">
        {notifications.data.map((notification) => (
          <Notification key={notification.id} {...notification} />
        ))}
      </div>
      <Spacer className="h-6" />
      <p className="rounded-2xl bg-[#EFF5F4] p-4 text-center">
        <small>
          <Trans>
            Notifikace a zprávy mají životnost 30 dní. Následně jsou samy
            automaticky smazány z přehledu.
          </Trans>
        </small>
      </p>
      <Spacer className="h-6" />
    </FullscreenModalLayout>
  );
};

const NotificationState = () => {
  const state = useNotificationsState();

  switch (state) {
    case "granted":
      return (
        <p>
          <small>
            <Trans>Oznámení můžete zakázat v nastavení prohlížeče.</Trans>
          </small>
        </p>
      );
    case "denied":
      return (
        <p>
          <small>
            <Trans>Oznámení můžete povolit v nastavení prohlížeče.</Trans>
          </small>
        </p>
      );
    case "default":
      return (
        <Button
          onClick={() =>
            window.Notification.requestPermission().catch(logger.error)
          }
          variant="secondary"
        >
          <Trans>Zapnout oznámení</Trans>
        </Button>
      );
  }
};

const Notification = ({
  createdAt,
  id,
  title,
  body,
}: { id: number } & NotificationProps) => {
  const queryClient = useQueryClient();
  const archiveMutation = useMutation({
    mutationFn: ({ id }: { id: number }) => archiveNotification(id),
    onError: () => {
      toaster.error(
        { title: <Trans>Notifikaci se nepodařilo smazat</Trans> },
        {
          autoClose: 5000,
        },
      );
    },
    onSuccess: () => {
      queryClient.setQueryData(
        ["notifications"],
        (previous: Awaited<ReturnType<typeof getNotifications>>) =>
          previous.filter((notification) => notification.id !== id),
      );
    },
  });

  return (
    <NotificationComponent key={id}>
      <NotificationComponent.Icon />
      <NotificationComponent.Content>
        <NotificationComponent.Title>{title}</NotificationComponent.Title>
        <NotificationComponent.Body>{body}</NotificationComponent.Body>
      </NotificationComponent.Content>
      <NotificationComponent.Button
        disabled={archiveMutation.status === "pending"}
        onClick={() => archiveMutation.mutate({ id })}
      >
        {archiveMutation.status === "pending" ? (
          <Spinner className="h-4! w-4!" withHint={false} />
        ) : (
          <Trash />
        )}
      </NotificationComponent.Button>
      <NotificationComponent.CreatedAt>
        {DateTime.fromISO(createdAt)}
      </NotificationComponent.CreatedAt>
    </NotificationComponent>
  );
};

function notificationsQuery() {
  return queryOptions({
    queryFn: ({ signal }) => getNotifications({ signal }),
    queryKey: ["notifications"],
  });
}

export { NotificationList };
